/**
 * The journey ladder, in the order the business runs in.
 *
 * The source vocabulary is task categories — "New Leads", "Unplanned Visits" —
 * which say how a row was filed, not what happened. A reader has to translate
 * on every node. These stages are the translation: `New Leads` is the referrer
 * handing over patient information, which is where a journey begins, and the
 * ladder runs from there to the payout.
 *
 * Kept in step with `backend/apps/analytics/vocabulary.py`; the backend stamps
 * the number on every event and this file only decides how it looks.
 *
 * Ordinal, not a funnel — a journey can skip stages, and a later stage is not
 * a subset of an earlier one, so these counts must never be divided by each other.
 */
import {
  Building2, CheckCircle2, Handshake, IndianRupee, Inbox, MessageCircle,
  Receipt, Target, UserPlus, type LucideIcon,
} from "lucide-react";

export interface Stage {
  n: number;
  name: string;
  meaning: string;
  color: string;
  bg: string;
  Icon: LucideIcon;
}

export const STAGES: Stage[] = [
  { n: 1, name: "Onboarded", meaning: "Referrer signed up, with commission terms agreed",
    color: "#6B6459", bg: "#F0EEEA", Icon: UserPlus },
  { n: 2, name: "Information", meaning: "Referrer handed over patient information",
    color: "#2563C9", bg: "#E8F0FC", Icon: Inbox },
  { n: 3, name: "Engagement", meaning: "Visited, called or hosted the referrer",
    color: "#7A5AF8", bg: "#F1EEFE", Icon: Handshake },
  { n: 4, name: "Interest", meaning: "Referrer responded positively",
    color: "#0E9AA0", bg: "#E4F5F6", Icon: MessageCircle },
  { n: 5, name: "Commitment", meaning: "Referrer committed to refer",
    color: "#4F46E5", bg: "#ECEBFD", Icon: CheckCircle2 },
  { n: 6, name: "Lead", meaning: "Lead record raised in Leadify",
    color: "#C2670A", bg: "#FBF0E2", Icon: Target },
  { n: 7, name: "Conversion", meaning: "Patient admitted",
    color: "#2F6B3C", bg: "#E7F1E9", Icon: Building2 },
  { n: 8, name: "Revenue", meaning: "Bill raised against the admission",
    color: "#B4690E", bg: "#FBF1E3", Icon: Receipt },
  { n: 9, name: "Payout", meaning: "Commission calculated, approved and paid",
    color: "#0B7A5E", bg: "#E4F2ED", Icon: IndianRupee },
];

export const STAGE_BY_N = new Map(STAGES.map((s) => [s.n, s]));

/** Falls back to Engagement so an unstamped event still renders. */
export function stageOf(n: number | undefined): Stage {
  return STAGE_BY_N.get(n ?? 3) ?? STAGES[2]!;
}
